// Единственное место, где приложение трогает window.Telegram.WebApp.
// initData не логируем и не сохраняем: они уходят только в POST /auth/telegram.

interface TelegramBackButton {
  show: () => void
  hide: () => void
  onClick: (cb: () => void) => void
  offClick: (cb: () => void) => void
}

interface TelegramHaptic {
  notificationOccurred: (type: 'error' | 'success' | 'warning') => void
  impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
}

interface Inset {
  top: number
  bottom: number
}

type InsetEvent = 'fullscreenChanged' | 'safeAreaChanged' | 'contentSafeAreaChanged'

interface TelegramWebApp {
  initData: string
  platform: string
  isFullscreen?: boolean
  safeAreaInset?: Inset
  contentSafeAreaInset?: Inset
  ready: () => void
  expand: () => void
  isVersionAtLeast?: (version: string) => boolean
  requestFullscreen?: () => void
  disableVerticalSwipes?: () => void
  setHeaderColor?: (color: string) => void
  setBackgroundColor?: (color: string) => void
  onEvent?: (event: InsetEvent, cb: () => void) => void
  openTelegramLink?: (url: string) => void
  BackButton?: TelegramBackButton
  HapticFeedback?: TelegramHaptic
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

function webApp(): TelegramWebApp | null {
  const app = window.Telegram?.WebApp
  // SDK грузится и вне Telegram: там initData пустые и platform "unknown"
  if (app === undefined || app.initData === '') return null
  return app
}

export function isInTelegram(): boolean {
  return webApp() !== null
}

/** Свежие initData текущего запуска или `null` вне Telegram. */
export function getInitData(): string | null {
  return webApp()?.initData ?? null
}

const MOBILE_PLATFORMS = new Set(['ios', 'android', 'android_x'])
const NO_INSET: Inset = { top: 0, bottom: 0 }

/** Отступы под вырез, полоску «домой» и, в полноэкранном режиме, под кнопки Telegram «Закрыть» и «⋯». */
function applyInsets(app: TelegramWebApp): void {
  const safe = app.safeAreaInset ?? NO_INSET
  const content = app.isFullscreen === true ? (app.contentSafeAreaInset ?? NO_INSET) : NO_INSET
  const root = document.documentElement
  root.style.setProperty('--inset-top', `${safe.top + content.top}px`)
  root.style.setProperty('--inset-bottom', `${safe.bottom + content.bottom}px`)
  root.dataset.fullscreen = app.isFullscreen === true ? 'true' : 'false'
}

/**
 * Запуск Mini App, как в lunary: на телефоне всегда на весь экран и без закрытия свайпом вниз,
 * из какой бы кнопки его ни открыли; шапка и фон Telegram в цвет приложения.
 */
export function bootWebApp(): void {
  const app = webApp()
  if (app === null) return
  // В Mini App всегда мобильный кадр, десктопная раскладка веба его не трогает
  document.documentElement.dataset.tg = 'true'
  app.ready()
  app.expand()
  if (MOBILE_PLATFORMS.has(app.platform) && app.isVersionAtLeast?.('8.0') === true) {
    app.requestFullscreen?.()
    app.disableVerticalSwipes?.()
  }
  applyInsets(app)
  const insets = () => applyInsets(app)
  app.onEvent?.('fullscreenChanged', insets)
  app.onEvent?.('safeAreaChanged', insets)
  app.onEvent?.('contentSafeAreaChanged', insets)
  const bg = getComputedStyle(document.documentElement).getPropertyValue('--c-bg').trim()
  if (bg !== '') {
    app.setHeaderColor?.(bg)
    app.setBackgroundColor?.(bg)
  }
}

export function haptic(type: 'error' | 'success' | 'warning'): void {
  webApp()?.HapticFeedback?.notificationOccurred(type)
}

/** Кнопка «назад» Telegram; возвращает функцию снятия. Вне Telegram ничего не делает. */
export function showBackButton(onBack: () => void): () => void {
  const button = webApp()?.BackButton
  if (button === undefined) return () => undefined
  button.onClick(onBack)
  button.show()
  return () => {
    button.offClick(onBack)
    button.hide()
  }
}
